import { redisClient } from "../../redis"

// Server-only env (never NEXT_PUBLIC_): this module must not reach a browser bundle.
const UPLEAP_USERNAME = process.env.UPLEAP_LOGIN_EMAIL
const UPLEAP_PASSWORD = process.env.UPLEAP_LOGIN_PASSWORD

const REDIS_TOKEN_KEY = "upleap:token"
const REDIS_TOKEN_EXP_KEY = "upleap:token_exp"
const TOKEN_TTL_SECONDS = 6 * 24 * 60 * 60 // 6 days

class UpleapAPIKeyManager {
    private static instance: UpleapAPIKeyManager
    private token: string | null = null
    private expirationTime: number = 0

    private constructor() {}

    public static getInstance(): UpleapAPIKeyManager {
        if (!UpleapAPIKeyManager.instance) {
            UpleapAPIKeyManager.instance = new UpleapAPIKeyManager()
        }
        return UpleapAPIKeyManager.instance
    }

    public async getToken(): Promise<string> {
        if (!this.token || Date.now() > this.expirationTime) {
            await this.refreshToken()
        }
        return this.token!
    }

    public invalidateToken(): void {
        this.token = null
        this.expirationTime = 0
        redisClient.del(REDIS_TOKEN_KEY).catch(() => {})
        redisClient.del(REDIS_TOKEN_EXP_KEY).catch(() => {})
        console.log("[UpleapAPIKeyManager] Token invalidated (in-memory + Redis)")
    }

    private async refreshToken(): Promise<void> {
        try {
            const [storedToken, storedExp] = await Promise.all([redisClient.get(REDIS_TOKEN_KEY), redisClient.get(REDIS_TOKEN_EXP_KEY)])
            if (storedToken && storedExp) {
                const exp = Number(storedExp)
                if (Date.now() < exp) {
                    this.token = storedToken as string
                    this.expirationTime = exp
                    console.log("[UpleapAPIKeyManager] Token restored from Redis")
                    return
                }
            }

            if (!UPLEAP_USERNAME || !UPLEAP_PASSWORD) {
                throw new Error("Missing UPLEAP_LOGIN_EMAIL / UPLEAP_LOGIN_PASSWORD")
            }

            console.log("[UpleapAPIKeyManager] Logging in to Upleap...")
            const response = await fetch("https://api.upleap.com/api/v1/oauth/login", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json"
                },
                body: JSON.stringify({
                    username: UPLEAP_USERNAME,
                    password: UPLEAP_PASSWORD
                })
            })

            if (!response.ok) {
                throw new Error(`Upleap login failed: ${response.status} ${response.statusText}`)
            }

            const data = await response.json()

            if (data.status === "ok" && data.token) {
                this.token = data.token
                this.expirationTime = Date.now() + TOKEN_TTL_SECONDS * 1000
                console.log("[UpleapAPIKeyManager] Token refreshed successfully via login")

                redisClient.setex(REDIS_TOKEN_KEY, TOKEN_TTL_SECONDS, this.token).catch(() => {})
                redisClient.setex(REDIS_TOKEN_EXP_KEY, TOKEN_TTL_SECONDS, String(this.expirationTime)).catch(() => {})
            } else {
                throw new Error(`Upleap login returned unexpected response: ${JSON.stringify(data)}`)
            }
        } catch (error) {
            console.error("[UpleapAPIKeyManager] Error refreshing token:", error)
            throw error
        }
    }
}

export default UpleapAPIKeyManager
